import { CirclePlayIcon, MoneyOffRounded as MoneyOffRoundedIcon } from 'components/icons';
import Chip from 'components/@extended/Chip.tsx';
import StatusChip from 'components/@extended/StatusChip';
import { OFF_OUTLINED_CLASS, OFF_SECONDARY_CLASS } from 'components/buttons/buttonStyle';
import { CallForActionButtonTooltip } from 'components/buttons/CallForActionButton';
import DisplayHistoryButton from 'components/buttons/DisplayHistoryButton';
import PermissionButton from 'components/buttons/PermissionButton';
import DmsLink from 'components/dmslink/DmsLink.tsx';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import type { DraftActions } from 'components/drafts/useDraftActions';
import MatterIndicators from 'components/matters/MatterIndicators';
import Loader from 'components/Loader';
import { FormattedDate } from 'components/misc/FormattedDate';
import PinButton from 'components/buttons/PinButton';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { MenuItem } from 'components/navigation/KebabMenu';
import PageHeader from 'components/PageHeader';
import PartyName from 'components/party/PartyName';
import { EmployeeNameLink } from 'components/record-chip/RecordLink';
import {
    DMS_META_CLASS,
    META_LINK_CLASS,
    META_ROW_CLASS,
    MetaItem,
    OUTLINED_ACTION_CLASS,
    PRIMARY_ACTION_CLASS,
    RecordMoreMenu,
} from 'components/record-header/RecordHeader';
import { Tooltip } from 'components/ui/tooltip';
import CopyToClipboardIcon from 'components/util/CopyToClipboard';
import useAuth from 'hooks/useAuth';
import { FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { useAddAndStartTimecard } from 'pages/timecards/useAddAndStartTimecard';
import { ReactNode, useId } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetInvoicesWithFiltersQuery } from 'store/api/invoices';
import { useDeleteMatterMutation, useLazyGetMatterDeleteValidationQuery } from 'store/api/matters';
import { useGetPaginatedTimecardsWithFiltersQuery } from 'store/api/timecards';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features.ts';
import { HistoryEntityType, HistoryFieldType } from 'types/histories';
import { MatterAddPrefill, MatterDeleteValidation, MatterDto, MatterStateType } from 'types/matters';
import { errorAlert, warningAlert } from 'utils/alert';
import { useBreadcrumbLeaf } from 'utils/breadcrumbLeaf';
import { getClassifierText, t } from 'utils/getTranslations';
import { NamedEntityParts } from 'utils/nameOrder';
import { useHasPermissions } from 'utils/permissionUtil';
import { recordPin } from 'utils/pins';
import { formatPracticeAreaLabel } from 'utils/practiceAreaUtil';
import { cn } from 'utils/cn';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { useLeaveDeletedEntity, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import Blocked from './Blocked.tsx';
import EditableMatterCaseNumber from './components/EditableMatterCaseNumber';
import { buildDeleteBlockerMessage } from './matterDeleteValidationMessage';
import OverdueInvoices from './OverdueInvoices';
import { useCloseCaseMenuItem } from './closing/CloseCaseMenuItem';
import { useReopenAsOfferPending } from './useReopenAsOfferPending';
import { getStatusChipType, matterNameWithoutNumber } from './util';

interface Props {
    matter: MatterDto;
    classifiers: Record<string, ClassifierData>;
    onOpenHistory: () => void;
}

const CREATE_TIMECARDS = ['CREATE.TIMECARDS'];
const CREATE_TIMECARDS_NON_ACCESSIBLE = ['CREATE.TIMECARDS.NON_ACCESSIBLE_MATTERS'];
const CREATE_MATTERS = ['CREATE.MATTERS', 'CREATE.MATTERS.LEADS', 'CREATE.MATTERS.CLIENTS'];
const READ_AUDIT_TRAIL = ['READ.MATTERS.AUDIT_TRAIL'];

const Abbreviation = ({ short, full }: { short: string; full: string }) => (
    <abbr title={full} className="no-underline">
        {short}
    </abbr>
);

/**
 * The AML chip. Its explanation is a tooltip, so the chip is a Tab stop: focus opens what hover opens, and Esc
 * closes it. The same words are its description, as the tooltip is in the page only while it shows.
 */
function AmlChip() {
    const tipId = useId();
    const tip = t('matters.aml-applicable-tooltip');
    return (
        <>
            <Tooltip content={tip}>
                <span tabIndex={0} aria-describedby={tipId} className={cn('inline-flex rounded-lg', FOCUS_RING)}>
                    <StatusChip type={'warning'} label={t('matters.aml-applicable')} />
                </span>
            </Tooltip>
            <span id={tipId} hidden>
                {tip}
            </span>
        </>
    );
}

/**
 * The chips after the case number: every chip first, then the state's history icon, so the icon never sits
 * between two chips; the pin star follows the row.
 */
export const MatterHeaderChips = ({
    matter,
    classifiers,
    hasOverdueInvoices,
}: {
    matter: MatterDto;
    classifiers: Record<string, ClassifierData>;
    hasOverdueInvoices: boolean;
}) => (
    <div className="flex flex-wrap items-center gap-1.5">
        {matter.isDraft ? <DraftChip /> : null}
        <MatterIndicators isConfidential={matter.isConfidential} hasAccess={!matter.isRestricted} variant="pill" />
        {matter.isRestricted ? null : (
            <>
                {matter.isNonBillable ? <Chip size="small" label={<MoneyOffRoundedIcon />} type={'partyType'} /> : null}
                <StatusChip
                    type={getStatusChipType(matter.matterStateTypeCode)}
                    label={getClassifierText(classifiers['MATTER_STATE_TYPE'], matter.matterStateTypeCode)}
                />
            </>
        )}
        {matter.isAmlApplicable && <AmlChip />}
        {matter.isCurrentlyBlocked && !matter.isRestricted && (
            <Blocked tooltipText={'dashboard.tooltips.timecard-blocked'} />
        )}
        {hasOverdueInvoices && <OverdueInvoices tooltipText="matters.has-overdue-invoices" />}
        {matter.isRestricted || matter.isDraft ? null : (
            <DisplayHistoryButton
                entityType={HistoryEntityType.MATTER}
                entityId={matter.id.toString()}
                fieldType={HistoryFieldType.MATTER_STATE_TYPE_CODE}
                fieldName={t('matters.state')}
                activity={t('updating')}
            />
        )}
    </div>
);

/**
 * The case header of the new design: number, chips and state on a first line, the full name, a meta row
 * that wraps, and three actions at most. It shows and gates what the classic `MatterPageHeader` does, except
 * Edit: each section of the case edits in place.
 */
export const MatterRecordHeader = ({ matter, classifiers, onOpenHistory }: Props): JSX.Element => {
    const { openModal, closeModal } = useModal();
    const navigateWithReturn = useNavigateWithReturnState();
    const leaveDeleted = useLeaveDeletedEntity('/matters');
    const [deleteMatter, { isLoading: deleteMatterLoading }] = useDeleteMatterMutation();
    const [fetchDeleteValidation] = useLazyGetMatterDeleteValidationQuery();
    const canCreateTimecardsOnNonAccessibleMatters = useHasPermissions(CREATE_TIMECARDS_NON_ACCESSIBLE);
    // The resulting case inherits the source case's client, so which of the three create grants applies
    // depends on that client's relationship type — the backend re-checks it against the party on save.
    const canCreateMatters = useHasPermissions(CREATE_MATTERS);
    const canReadAuditTrail = useHasPermissions(READ_AUDIT_TRAIL);
    const { addAndStartTimecard, isSubmitting: isStartingTimecard } = useAddAndStartTimecard(matter);
    const isOfferFlowEnabled = useIsFeatureEnabled(FeatureFlag.CASE_OFFER_FLOW);
    const reopenMenuItem = useReopenAsOfferPending(matter);
    const closeCaseMenuItem = useCloseCaseMenuItem(matter);
    // The crumb names the case by its number; the record's name, which the AI panel reads, stays the full name.
    useBreadcrumbLeaf(matter.fullName, matter.caseNumber || undefined);

    // Once the user tracks this case, Start timer is hidden rather than disabled, so no second draft is
    // made; it stays hidden until that is known, so a click during the load cannot slip past.
    const { user } = useAuth();
    const { data: ownDraftTimecards, isLoading: isLoadingOwnDrafts } = useGetPaginatedTimecardsWithFiltersQuery(
        {
            page: 0,
            pageSize: 1,
            filters: `createdByEmployee.id==(${user?.id});timecardStatusTypeCode=in=(DRAFT);matter.id==(${matter.id})`,
        },
        { skip: !user?.id }
    );
    const hasOwnDraftTimecard = isLoadingOwnDrafts || (ownDraftTimecards?.totalElements ?? 0) > 0;

    const currentDate = new Date().toISOString().split('T')[0];
    const { data: overdueInvoices } = useGetInvoicesWithFiltersQuery({
        filters: `invoiceTypeCode=in=(DEBIT,PREPAYMENT);dueDate=duedateispast=${currentDate};invoiceStatusTypeCode=in=(SENT);id=ismatterinvoice=${matter.id}`,
        page: 0,
        pageSize: 1,
    });
    const { matterStateTypeCode } = matter;
    const isCaseClosed =
        matterStateTypeCode === MatterStateType.COMPLETED ||
        matterStateTypeCode === MatterStateType.OFFER_LOST ||
        matterStateTypeCode === MatterStateType.OFFER_WON;

    const isClientPersonaNonGrata = matter.clientParty?.isPersonaNonGrata === true;
    // Why time cannot be entered, in words the record already has: the client's, else those of the blocked notice,
    // which the General tab alone prints. A closed case has no such words: its state chip says it.
    const blockedReason = matter.isCurrentlyBlocked ? t('dashboard.tooltips.timecard-blocked') : undefined;
    const addTimecardDisabledTooltipText = isClientPersonaNonGrata
        ? t('contacts.tooltips.add-new-case-disabled-persona-non-grata')
        : blockedReason;
    const canAddTimecards = !matter.isRestricted || canCreateTimecardsOnNonAccessibleMatters;
    const isTimeEntryClosed = matter.isCurrentlyBlocked || isCaseClosed || isClientPersonaNonGrata;
    const timeEntryReasonId = useId();
    const describedByReason = addTimecardDisabledTooltipText ? timeEntryReasonId : undefined;

    const deleteMatterHandler = async () => {
        if (!matter.id || isNaN(Number(matter.id))) {
            return;
        }
        let validation: MatterDeleteValidation;
        try {
            validation = await fetchDeleteValidation({ id: `${matter.id}` }).unwrap();
        } catch (e) {
            console.error('matter delete-validation fetch failed: ', e);
            errorAlert(t('matters.error-checking-deletability'));
            return;
        }
        const blockerMessage = buildDeleteBlockerMessage(validation);
        if (blockerMessage) {
            warningAlert(blockerMessage);
            return;
        }
        openModal(
            <ConfirmationModal
                titleId="matters.delete-title"
                titleValues={{ name: matter.name }}
                confirmId="matters.delete"
                onCancel={closeModal}
                onConfirm={() => {
                    void deleteMatter({ id: `${matter.id}` })
                        .unwrap()
                        .then(() => {
                            leaveDeleted();
                            closeModal();
                        })
                        // The API's error handler toasts the failure.
                        .catch(console.error);
                }}
                content={<p className="text-body1">{t('matters.delete-body')}</p>}
            />
        );
    };

    // An Offer Won case can only ever produce one resulting case, so the action goes away once it exists.
    const canOpenNewConnectedCase =
        isOfferFlowEnabled &&
        matterStateTypeCode === MatterStateType.OFFER_WON &&
        !matter.connectedCase &&
        !matter.isRestricted &&
        canCreateMatters;

    const menuItems: MenuItem[] = [];
    if (closeCaseMenuItem) {
        menuItems.push(closeCaseMenuItem);
    }
    if (!matter.isRestricted && canReadAuditTrail) {
        menuItems.push({ handler: onOpenHistory, title: t('audit-trail.matter.button') });
    }
    if (canOpenNewConnectedCase) {
        menuItems.push({
            handler: () => {
                const prefill: MatterAddPrefill = { originatesFromMatterId: matter.id };
                navigateWithReturn('/matter/new', { state: { prefill } });
            },
            title: t('matters.open-new-connected-case'),
        });
    }
    if (isOfferFlowEnabled && reopenMenuItem) {
        menuItems.push(reopenMenuItem);
    }
    if (matter.isDeletable) {
        menuItems.push({
            handler: () => {
                void deleteMatterHandler();
            },
            title: t('matters.delete'),
            variant: 'destructive',
        });
    }

    if (deleteMatterLoading) {
        return <Loader />;
    }

    const practiceAreas = (matter.practiceAreas ?? [])
        .map(formatPracticeAreaLabel)
        .toSorted((a, b) => a.localeCompare(b))
        .join(', ');

    // Under 768px the actions take a row of their own under the meta row, from the left: beside the title they
    // would run past the screen's edge. Start timer and Log time stay focusable while off (`focusableWhenDisabled`
    // makes them `aria-disabled`, and the kit button then takes no click), so the tooltip that says why opens on
    // focus as on hover, on the button itself; the reason is their description too.
    const actions = (
        <div className="flex flex-none flex-row items-center gap-2 max-sm:w-full sm:justify-end">
            {!hasOwnDraftTimecard && canAddTimecards && (
                <CallForActionButtonTooltip title={addTimecardDisabledTooltipText}>
                    <PermissionButton
                        permissions={CREATE_TIMECARDS}
                        variant="outlined"
                        size="small"
                        // Icon only between the rail's 768px and the open sidebar's 1280px, where the glyph keeps no
                        // icon-side trim and stays centred.
                        className={cn(
                            OUTLINED_ACTION_CLASS,
                            OFF_OUTLINED_CLASS,
                            'px-3.5 font-medium has-data-[icon=inline-start]:pl-3 sm:max-[1280px]:size-9 sm:max-[1280px]:px-0 sm:max-[1280px]:has-data-[icon=inline-start]:pl-0'
                        )}
                        startIcon={<CirclePlayIcon className="size-4" />}
                        onClick={() => {
                            void addAndStartTimecard();
                        }}
                        disabled={isTimeEntryClosed || isStartingTimecard}
                        focusableWhenDisabled
                        aria-describedby={describedByReason}
                    >
                        <span className="sm:max-[1280px]:sr-only">
                            <FormattedMessage id="menu.start-timer" />
                        </span>
                    </PermissionButton>
                </CallForActionButtonTooltip>
            )}
            {canAddTimecards && (
                <CallForActionButtonTooltip title={addTimecardDisabledTooltipText}>
                    <PermissionButton
                        permissions={CREATE_TIMECARDS}
                        variant="contained"
                        color="secondary"
                        size="small"
                        className={cn(PRIMARY_ACTION_CLASS, OFF_SECONDARY_CLASS)}
                        onClick={() => navigateWithReturn(`/matter/${matter.id}/timecards/new`)}
                        disabled={isTimeEntryClosed}
                        focusableWhenDisabled
                        aria-describedby={describedByReason}
                    >
                        <FormattedMessage id="global-search.peek.log-time" />
                    </PermissionButton>
                </CallForActionButtonTooltip>
            )}
            <RecordMoreMenu menuItems={menuItems} />
            {addTimecardDisabledTooltipText ? (
                <span id={timeEntryReasonId} hidden>
                    {addTimecardDisabledTooltipText}
                </span>
            ) : null}
        </div>
    );

    return (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="flex min-w-0 flex-auto flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2.5">
                    <span className="text-foreground-tertiary text-meta font-mono">
                        <EditableMatterCaseNumber matterId={matter.id} caseNumber={matter.caseNumber} />
                    </span>
                    <MatterHeaderChips
                        matter={matter}
                        classifiers={classifiers}
                        hasOverdueInvoices={!!overdueInvoices && overdueInvoices.totalElements > 0}
                    />
                    <span data-tour="record-pin" className="inline-flex">
                        {/* Pinned as the sidebar reads a case: its number, a dot, then its title. */}
                        <PinButton
                            pin={recordPin(
                                'matter',
                                matter.id,
                                matter.caseNumber
                                    ? `${matter.caseNumber} · ${matterNameWithoutNumber(matter.caseNumber, matter.fullName)}`
                                    : matter.fullName
                            )}
                        />
                    </span>
                </div>
                <PageHeader
                    title={matter.fullName}
                    titleNode={matterNameWithoutNumber(matter.caseNumber, matter.fullName)}
                    iconsEnd={matter.fullName ? <CopyToClipboardIcon text={matter.fullName} className="ml-0" /> : null}
                >
                    <div className={META_ROW_CLASS}>
                        <ClientMeta matter={matter} />
                        {matter.primaryContactParty?.id ? (
                            <MetaItem label={<FormattedMessage id="ai-chat.meeting-prep.info.primary-contact" />}>
                                <PartyName party={matter.primaryContactParty} isLink />
                            </MetaItem>
                        ) : null}
                        <EmployeeWithHistory
                            entity={matter.supervisedByEmployee}
                            matterId={matter.id}
                            label={<SpLabel />}
                            historyFieldType={HistoryFieldType.SUPERVISING_EMPLOYEE}
                            historyFieldName={t('matters.supervised-by')}
                        />
                        <EmployeeWithHistory
                            entity={matter.responsibleEmployee}
                            matterId={matter.id}
                            label={<RlLabel />}
                            historyFieldType={HistoryFieldType.RESPONSIBLE_EMPLOYEE}
                            historyFieldName={t('matters.responsible-lawyer')}
                        />
                        {practiceAreas ? (
                            <MetaItem label={<FormattedMessage id="practice-area" />}>{practiceAreas}</MetaItem>
                        ) : null}
                        {matter.openedDate ? (
                            <MetaItem label={<FormattedMessage id="matters.opened" />}>
                                <FormattedDate value={matter.openedDate} />
                            </MetaItem>
                        ) : null}
                        {matter.dmsLink ? (
                            <span className={DMS_META_CLASS}>
                                <DmsLink link={matter.dmsLink} label={t('open-in-dms')} isIconDisplayed={true} />
                            </span>
                        ) : null}
                    </div>
                </PageHeader>
            </div>
            {actions}
        </div>
    );
};

const ClientMeta = ({ matter }: { matter: MatterDto }) =>
    matter.clientParty?.id ? (
        <MetaItem label={<FormattedMessage id="matters.client" />}>
            <PartyName party={matter.clientParty} isLink showPartyNumber />
        </MetaItem>
    ) : null;

/** One of the case's people, linked; with a history field, the button that lists who held the role before. */
const EmployeeWithHistory = ({
    entity,
    matterId,
    label,
    historyFieldType,
    historyFieldName,
}: {
    entity: (NamedEntityParts & { id?: number }) | undefined | null;
    matterId: number;
    label: ReactNode;
    historyFieldType?: HistoryFieldType;
    historyFieldName?: string;
}) => {
    if (!entity?.id) return null;
    return (
        <MetaItem label={label}>
            <EmployeeNameLink className={META_LINK_CLASS} to={`/employee/${entity.id}`} entity={entity} />
            {historyFieldType ? (
                <DisplayHistoryButton
                    entityType={HistoryEntityType.MATTER}
                    entityId={matterId.toString()}
                    fieldType={historyFieldType}
                    fieldName={historyFieldName ?? ''}
                    activity={t('updating')}
                />
            ) : null}
        </MetaItem>
    );
};

const SpLabel = () => (
    <Abbreviation short={t('delegation.role-short.SUPERVISING_PARTNER')} full={t('matters.supervising-partner')} />
);
const RlLabel = () => (
    <Abbreviation short={t('delegation.role-short.RESPONSIBLE_LAWYER')} full={t('matters.responsible-lawyer')} />
);

/**
 * A draft's header in the new design: the Draft and state chips where the number goes, its title, the meta row
 * with who created it and when it was last saved, then Edit, Share and ⋯ › Delete draft as far as the reader's right
 * on the draft goes. Time entry, history and pinning wait for the case to be created.
 */
export const MatterDraftRecordHeader = ({
    matter,
    classifiers,
    actions,
}: {
    matter: MatterDto;
    classifiers: Record<string, ClassifierData>;
    actions: DraftActions;
}): JSX.Element => {
    useBreadcrumbLeaf(matter.fullName, undefined);
    const { draft } = actions;

    return (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="flex min-w-0 flex-auto flex-col gap-2">
                <MatterHeaderChips matter={matter} classifiers={classifiers} hasOverdueInvoices={false} />
                <PageHeader
                    title={matter.fullName}
                    iconsEnd={matter.fullName ? <CopyToClipboardIcon text={matter.fullName} className="ml-0" /> : null}
                >
                    <div className={META_ROW_CLASS}>
                        <ClientMeta matter={matter} />
                        <EmployeeWithHistory
                            entity={matter.supervisedByEmployee}
                            matterId={matter.id}
                            label={<SpLabel />}
                        />
                        <EmployeeWithHistory
                            entity={matter.responsibleEmployee}
                            matterId={matter.id}
                            label={<RlLabel />}
                        />
                        {draft ? <DraftMeta draft={draft} /> : null}
                    </div>
                </PageHeader>
            </div>
            <div className="flex flex-none flex-row items-center gap-2 max-sm:w-full sm:justify-end">
                <DraftRecordActions actions={actions} />
            </div>
        </div>
    );
};
